/**
 * @format
 */
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StateService } from '@uirouter/core';
import { CeeposPaymentConfirmation, StudentWorkflow } from 'common-typescript/types';
import { EMPTY, Observable, retry } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AlertsService, AlertType } from 'sis-components/alerts/alerts-ng.service';

@Injectable({ providedIn: 'root' })
export class StudyRightExtensionWorkflowPaymentService {
    private readonly http = inject(HttpClient);
    private readonly state = inject(StateService);
    private readonly alertService = inject(AlertsService);
    private readonly translateService = inject(TranslateService);

    /**
     * Sends the data from the external payment system to ORI for processing, and redirects the user to either the submitted workflow view
     * or to an error view, depending on the payment processing result.
     */
    completeCeeposPayment(paymentConfirmation: Partial<CeeposPaymentConfirmation>): void {
        const request$ = this.http.post<StudentWorkflow>(
            '/ori/api/study-right-extension/payment-system/complete-ceepos-payment',
            paymentConfirmation,
        );
        this.completePayment(request$);
    }

    /**
     * Sends the data from the external payment system to ORI for processing, and redirects the user to either the submitted workflow view
     * or to an error view, depending on the payment processing result.
     */
    completePaytrailPayment(
        queryParams?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> },
    ): void {
        const request$ = this.http.post<StudentWorkflow>('/ori/api/study-right-extension/payment-system/complete-paytrail-payment', null, {
            params: queryParams,
            // Angular doesn't set the Content-Type header if the body is null/undefined.
            // The backend throws an exception without the header, so need to set it manually.
            headers: { 'Content-Type': 'application/json' },
        });
        this.completePayment(request$);
    }

    private completePayment(completePaymentRequest$: Observable<StudentWorkflow>): void {
        completePaymentRequest$
            .pipe(
                retry({ count: 3, delay: 10000 }), // if error, wait 10 seconds and try the request again (max 3 times)
                catchError(e => {
                    // our backend could not process the payment, redirect to the payment processing page
                    console.error('Payment processing failed', e);
                    this.state.go('student.logged-in.profile.applications.study-right-extension-workflow-payment-processing');
                    return EMPTY;
                }),
            )
            .subscribe(workflow => {
                if (workflow?.documentState === 'ACTIVE') {
                    this.alertService.addAlert({
                        message: this.translateService.instant('PROFILE.APPLICATIONS.EXTENSION_APPLICATION.PAYMENT.PAYMENT_SUCCESSFUL'),
                        type: AlertType.SUCCESS,
                    });
                    this.state.go('student.logged-in.profile.applications.study-right-extension-workflow', { applicationId: workflow.id });
                } else {
                    // our backend processed the payment and the payment failed
                    this.state.go('student.logged-in.profile.applications.study-right-extension-workflow-payment-failed');
                }
            });
    }
}
